/**
 * ClubForm - Shared Form for Create/Edit Clubs
 *
 * Consolidates form logic from CreateClubPage and ClubEditPage.
 * Mode-based rendering handles differences between create and edit flows.
 *
 * Issue #1046: Refactored to reduce code duplication.
 */

import { useState, type FormEvent } from 'react';
import { Button, Input } from '../components';
import { HeroImageUpload } from './HeroImageUpload';
import { US_STATES } from '../constants/usStates';
import {
  Building2,
  MapPin,
  AlertTriangle,
  Eye,
  EyeOff,
  Lock,
  Globe,
  Zap,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface ClubFormData {
  name: string;
  description: string;
  meeting_mode: 'in_person' | 'virtual' | 'hybrid';
  city: string;
  state: string;
  region: 'americas' | 'emea' | 'apac' | null;
  visibility: 'public' | 'private' | 'members_only';
  primary_sport: string;
  secondary_sports: string[];
}

export interface ClubFormProps {
  mode: 'create' | 'edit';

  // Data
  initialData?: Partial<ClubFormData>;
  currentHeroUrl?: string | null;

  // Callbacks
  onSubmit: (data: ClubFormData) => void;
  onCancel: () => void;
  onHeroUpload?: (file: File) => Promise<string>;
  onHeroRemove?: () => Promise<void>;
  onHeroChange?: (file: File | null, previewUrl: string | null) => void; // Create mode - stores file for later

  // State from parent
  isSubmitting?: boolean;
  submitError?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const COMMON_SPORTS = [
  'Running', 'Trail Running', 'Marathon Training', 'Cycling', 'Mountain Biking',
  'Swimming', 'Triathlon', 'Rock Climbing', 'Hiking', 'CrossFit',
  'Yoga', 'Pilates', 'Tennis', 'Basketball', 'Soccer', 'Football',
  'Baseball', 'Softball', 'Golf', 'Skiing', 'Snowboarding', 'Surfing',
  'Volleyball', 'Badminton', 'Table Tennis', 'Wrestling', 'Boxing',
  'Martial Arts', 'Rowing', 'Kayaking', 'Stand-up Paddleboarding',
  'Fitness Training', 'Strength Training', 'Bodybuilding', 'Powerlifting',
  'Track and Field', 'Cross Country', 'Adventure Racing', 'Obstacle Racing'
];

// ============================================================================
// Component
// ============================================================================

export function ClubForm({
  mode,
  initialData,
  currentHeroUrl,
  onSubmit,
  onCancel,
  onHeroUpload,
  onHeroRemove,
  onHeroChange,
  isSubmitting = false,
  submitError,
}: ClubFormProps) {
  // Form state
  const [formData, setFormData] = useState<ClubFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    meeting_mode: initialData?.meeting_mode || 'in_person',
    city: initialData?.city || '',
    state: initialData?.state || '',
    region: initialData?.region || null,
    visibility: initialData?.visibility || 'members_only',
    primary_sport: initialData?.primary_sport || '',
    secondary_sports: initialData?.secondary_sports || [],
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [heroPreviewUrl, setHeroPreviewUrl] = useState<string | null>(currentHeroUrl || null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // React docs "adjust state on prop change" — sync form when initialData
  // changes (edit mode reloads).
  const [prevInitialData, setPrevInitialData] = useState(initialData);
  if (prevInitialData !== initialData) {
    setPrevInitialData(initialData);
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        meeting_mode: initialData.meeting_mode || 'in_person',
        city: initialData.city || '',
        state: initialData.state || '',
        region: initialData.region || null,
        visibility: initialData.visibility || 'members_only',
        primary_sport: initialData.primary_sport || '',
        secondary_sports: initialData.secondary_sports || [],
      });
    }
  }

  // Sync hero preview to currentHeroUrl prop changes.
  const [prevCurrentHeroUrl, setPrevCurrentHeroUrl] = useState(currentHeroUrl);
  if (prevCurrentHeroUrl !== currentHeroUrl) {
    setPrevCurrentHeroUrl(currentHeroUrl);
    setHeroPreviewUrl(currentHeroUrl || null);
  }

  // ============================================================================
  // Validation
  // ============================================================================

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Club name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Club name should be at least 3 characters';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Club name should be less than 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be under 500 characters';
    }

    // Location required only in edit mode for in_person and hybrid clubs (#635)
    if (mode === 'edit' && (formData.meeting_mode === 'in_person' || formData.meeting_mode === 'hybrid')) {
      if (!formData.city.trim()) {
        errors.city = 'City is required';
      }
      if (!formData.state) {
        errors.state = 'State is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleInputChange = (field: keyof ClubFormData, value: string | string[] | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (successMessage) {
      setSuccessMessage(null);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(formData);
  };

  // Hero image handlers for create mode
  const handleCreateModeHeroUpload = async (file: File): Promise<string> => {
    // Store file for upload after club creation
    const previewUrl = URL.createObjectURL(file);
    setHeroPreviewUrl(previewUrl);
    onHeroChange?.(file, previewUrl);
    return previewUrl;
  };

  const handleCreateModeHeroRemove = async () => {
    if (heroPreviewUrl && heroPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(heroPreviewUrl);
    }
    setHeroPreviewUrl(null);
    onHeroChange?.(null, null);
  };

  // Hero image handlers for edit mode (immediate upload)
  const handleEditModeHeroUpload = async (file: File): Promise<string> => {
    if (!onHeroUpload) throw new Error('Hero upload handler not provided');
    const url = await onHeroUpload(file);
    setHeroPreviewUrl(url);
    setSuccessMessage('Cover photo updated successfully');
    return url;
  };

  const handleEditModeHeroRemove = async () => {
    if (!onHeroRemove) throw new Error('Hero remove handler not provided');
    await onHeroRemove();
    setHeroPreviewUrl(null);
    setSuccessMessage('Cover photo removed successfully');
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} noValidate className="px-4 py-6 space-y-6">
      {/* Validation Error Summary */}
      {submitError && (
        <div className="bg-oro-error/10 border border-oro-error/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-oro-error flex-shrink-0 mt-0.5" />
          <p className="text-oro-error text-sm">{submitError}</p>
        </div>
      )}

      {/* Hero Image Upload */}
      <div className={mode === 'edit' ? 'bg-oro-surface border border-oro-border rounded-xl p-6' : 'space-y-2'}>
        {mode === 'edit' && (
          <>
            <h3 className="font-medium text-text-primary mb-1">Cover Image</h3>
            <p className="text-sm text-text-secondary mb-4">
              Upload a cover photo for your club. Recommended size: 1200×400px.
            </p>
          </>
        )}
        {mode === 'create' && (
          <label className="block text-sm font-medium text-text-secondary">
            Club Cover Photo <span className="text-text-muted font-normal">(optional)</span>
          </label>
        )}
        <HeroImageUpload
          currentImageUrl={heroPreviewUrl}
          onImageChange={() => {}}
          onImageUpload={mode === 'create' ? handleCreateModeHeroUpload : handleEditModeHeroUpload}
          onImageRemove={mode === 'create' ? handleCreateModeHeroRemove : handleEditModeHeroRemove}
          enableCrop={true}
          compact={mode === 'create'}
          className="w-full"
          disabled={isSubmitting}
        />
        {mode === 'create' && (
          <p className="text-xs text-text-muted">
            Optional: Upload a cover photo for your club
          </p>
        )}
        {mode === 'edit' && successMessage && (
          <div className="mt-4 bg-oro-success/10 border border-oro-success/30 rounded-lg p-3">
            <p className="text-oro-success text-sm">{successMessage}</p>
          </div>
        )}
      </div>

      {/* Club Details */}
      <div className={mode === 'edit' ? 'bg-oro-surface border border-oro-border rounded-xl p-4' : 'space-y-4'}>
        {mode === 'edit' && (
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-5 h-5 text-text-muted" />
            <h3 className="font-medium text-text-primary">Club Details</h3>
          </div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-text-secondary">
              Club Name {mode === 'create' && <span className="text-accent">*</span>}
              {mode === 'edit' && '*'}
            </label>
            <Input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isSubmitting}
              placeholder={mode === 'create' ? 'My Club' : 'Enter club name'}
              required
              maxLength={100}
              size="lg"
              className={validationErrors.name ? 'border-oro-error' : ''}
            />
            {validationErrors.name && (
              <p className="text-xs text-oro-error">{validationErrors.name}</p>
            )}
            <p className="text-xs text-text-muted">
              {mode === 'create'
                ? 'Choose something memorable and unique'
                : `${formData.name.length}/100`}
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={isSubmitting}
              rows={4}
              maxLength={500}
              placeholder={mode === 'create'
                ? 'We run. We talk. We drink coffee.'
                : "Describe your club's mission, activities, and community"}
              className={`w-full px-4 py-3 rounded-lg border ${
                validationErrors.description ? 'border-oro-error' : 'border-oro-border'
              } bg-oro-surface text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 disabled:cursor-not-allowed resize-none transition-colors`}
            />
            {validationErrors.description && (
              <p className="text-xs text-oro-error">{validationErrors.description}</p>
            )}
            <p className="text-xs text-text-muted">
              {mode === 'create'
                ? 'Tell people what your club is about (optional)'
                : `${formData.description.length}/500`}
            </p>
          </div>
        </div>
      </div>

      {/* Meeting Mode (#635: Virtual clubs) */}
      <div className={mode === 'edit' ? 'bg-oro-surface border border-oro-border rounded-xl p-4' : 'space-y-2'}>
        {mode === 'edit' && (
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-text-muted" />
            <h3 className="font-medium text-text-primary">How does your club meet?</h3>
          </div>
        )}
        {mode === 'create' && (
          <label className="block text-sm font-medium text-text-secondary">
            How does your club meet?
          </label>
        )}
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => handleInputChange('meeting_mode', 'in_person')}
            disabled={isSubmitting}
            className={`p-3 border rounded-lg text-left transition-all ${
              formData.meeting_mode === 'in_person'
                ? 'border-accent bg-accent/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <MapPin className="w-4 h-4 text-text-secondary mb-1" />
            <div className="font-medium text-text-primary text-sm">In Person</div>
            <p className="text-xs text-text-muted mt-0.5">
              Local meetups
            </p>
          </button>
          <button
            type="button"
            onClick={() => handleInputChange('meeting_mode', 'virtual')}
            disabled={isSubmitting}
            className={`p-3 border rounded-lg text-left transition-all ${
              formData.meeting_mode === 'virtual'
                ? 'border-accent bg-accent/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Globe className="w-4 h-4 text-text-secondary mb-1" />
            <div className="font-medium text-text-primary text-sm">Virtual</div>
            <p className="text-xs text-text-muted mt-0.5">
              Online only
            </p>
          </button>
          <button
            type="button"
            onClick={() => handleInputChange('meeting_mode', 'hybrid')}
            disabled={isSubmitting}
            className={`p-3 border rounded-lg text-left transition-all ${
              formData.meeting_mode === 'hybrid'
                ? 'border-accent bg-accent/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Zap className="w-4 h-4 text-text-secondary mb-1" />
            <div className="font-medium text-text-primary text-sm">Hybrid</div>
            <p className="text-xs text-text-muted mt-0.5">
              Both options
            </p>
          </button>
        </div>
      </div>

      {/* Location - for in_person and hybrid clubs */}
      {(formData.meeting_mode === 'in_person' || formData.meeting_mode === 'hybrid') && (
        <div className={mode === 'edit' ? 'bg-oro-surface border border-oro-border rounded-xl p-4' : 'space-y-2'}>
          {mode === 'edit' && (
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-5 h-5 text-text-muted" />
              <h3 className="font-medium text-text-primary">Location</h3>
            </div>
          )}
          {mode === 'create' && (
            <label className="block text-sm font-medium text-text-secondary">
              Location <span className="text-text-muted font-normal">(optional)</span>
            </label>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              {mode === 'edit' && (
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  City *
                </label>
              )}
              <Input
                type="text"
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                disabled={isSubmitting}
                placeholder="City"
                maxLength={100}
                size="lg"
                className={validationErrors.city ? 'border-oro-error' : ''}
              />
              {validationErrors.city && (
                <p className="mt-1 text-xs text-oro-error">{validationErrors.city}</p>
              )}
            </div>
            <div>
              {mode === 'edit' && (
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  State *
                </label>
              )}
              <select
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 rounded-lg border ${
                  validationErrors.state ? 'border-oro-error' : 'border-oro-border'
                } bg-oro-surface text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                <option value="">State</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {validationErrors.state && (
                <p className="mt-1 text-xs text-oro-error">{validationErrors.state}</p>
              )}
            </div>
          </div>
          <p className="text-xs text-text-muted mt-2">
            {mode === 'create' ? 'Help members find your club locally' : ''}
          </p>

          {mode === 'edit' && formData.city && formData.state && (
            <div className="mt-3 p-3 bg-accent/10 rounded-lg">
              <p className="text-xs text-accent">
                <strong>Preview:</strong> {formData.city}, {formData.state}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Region - for virtual and hybrid clubs */}
      {(formData.meeting_mode === 'virtual' || formData.meeting_mode === 'hybrid') && (
        <div className={mode === 'edit' ? 'bg-oro-surface border border-oro-border rounded-xl p-4' : 'space-y-2'}>
          {mode === 'edit' && (
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-5 h-5 text-text-muted" />
              <h3 className="font-medium text-text-primary">Primary Region</h3>
            </div>
          )}
          {mode === 'create' && (
            <label className="block text-sm font-medium text-text-secondary">
              Primary Region <span className="text-text-muted font-normal">(optional)</span>
            </label>
          )}
          <select
            id="region"
            value={formData.region || ''}
            onChange={(e) => handleInputChange('region', e.target.value || null)}
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface text-text-primary focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <option value="">Global (no specific region)</option>
            <option value="americas">Americas</option>
            <option value="emea">Europe, Middle East & Africa</option>
            <option value="apac">Asia Pacific</option>
          </select>
          <p className="text-xs text-text-muted mt-2">
            Helps members find clubs active in their timezone
          </p>
          <div className="mt-3 p-3 bg-accent/10 rounded-lg">
            <p className="text-xs text-accent">
              Event times will display in each member's local timezone
            </p>
          </div>
        </div>
      )}

      {/* Visibility Settings (Edit mode only) */}
      {mode === 'edit' && (
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <Eye className="w-5 h-5 text-text-muted" />
            <h3 className="font-medium text-text-primary">Club Visibility</h3>
          </div>

          <div className="space-y-3">
            {/* Public Option */}
            <label
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                formData.visibility === 'public'
                  ? 'border-accent bg-accent/5'
                  : 'border-oro-border hover:border-oro-border-hover'
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={formData.visibility === 'public'}
                onChange={(e) => handleInputChange('visibility', e.target.value)}
                className="mt-1 accent-accent"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-text-secondary" />
                  <span className="font-medium text-text-primary">Public</span>
                </div>
                <p className="text-sm text-text-secondary mt-1">
                  Anyone can discover and join your club. Visible in search results and club listings.
                </p>
              </div>
            </label>

            {/* Invite Only Option */}
            <label
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                formData.visibility === 'members_only'
                  ? 'border-accent bg-accent/5'
                  : 'border-oro-border hover:border-oro-border-hover'
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value="members_only"
                checked={formData.visibility === 'members_only'}
                onChange={(e) => handleInputChange('visibility', e.target.value)}
                className="mt-1 accent-accent"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-text-secondary" />
                  <span className="font-medium text-text-primary">Invite Only</span>
                </div>
                <p className="text-sm text-text-secondary mt-1">
                  Visible in search, but new members must request to join or receive an invite.
                </p>
              </div>
            </label>

            {/* Private Option */}
            <label
              className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                formData.visibility === 'private'
                  ? 'border-accent bg-accent/5'
                  : 'border-oro-border hover:border-oro-border-hover'
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={formData.visibility === 'private'}
                onChange={(e) => handleInputChange('visibility', e.target.value)}
                className="mt-1 accent-accent"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-text-secondary" />
                  <span className="font-medium text-text-primary">Private</span>
                </div>
                <p className="text-sm text-text-secondary mt-1">
                  Hidden from search and listings. Only visible to existing members.
                </p>
              </div>
            </label>
          </div>

          <div className="mt-4 p-3 bg-oro-surface-alt rounded-lg">
            <p className="text-xs text-text-muted">
              Changing visibility does not affect existing members. They will remain in the club.
            </p>
          </div>
        </div>
      )}

      {/* Sports & Activities (Edit mode only) */}
      {mode === 'edit' && (
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-5 h-5 text-text-muted" />
            <h3 className="font-medium text-text-primary">Sports & Activities</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Primary Sport
              </label>
              <select
                value={formData.primary_sport}
                onChange={(e) => handleInputChange('primary_sport', e.target.value)}
                className="w-full px-3 py-2 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary focus:ring-2 focus:ring-accent focus:border-accent"
              >
                <option value="">Select primary sport</option>
                {COMMON_SPORTS.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Secondary Sports (Optional)
              </label>
              <div className="space-y-2">
                {formData.secondary_sports.map((sport, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      value={sport}
                      onChange={(e) => {
                        const newSports = [...formData.secondary_sports];
                        newSports[index] = e.target.value;
                        handleInputChange('secondary_sports', newSports);
                      }}
                      className="flex-1 px-3 py-2 bg-oro-surface-alt border border-oro-border rounded-lg text-text-primary focus:ring-2 focus:ring-accent focus:border-accent"
                    >
                      <option value="">Select sport</option>
                      {COMMON_SPORTS.filter(s => s !== formData.primary_sport && !formData.secondary_sports.includes(s)).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        const newSports = formData.secondary_sports.filter((_, i) => i !== index);
                        handleInputChange('secondary_sports', newSports);
                      }}
                      className="p-2 text-oro-error hover:text-oro-error/80 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
                {formData.secondary_sports.length < 5 && (
                  <button
                    type="button"
                    onClick={() => {
                      const newSports = [...formData.secondary_sports, ''];
                      handleInputChange('secondary_sports', newSports);
                    }}
                    className="w-full px-3 py-2 border-2 border-dashed border-oro-border rounded-lg text-text-muted hover:text-accent hover:border-accent transition-colors"
                  >
                    + Add Secondary Sport
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex flex-col gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? (mode === 'create' ? 'Creating...' : 'Saving Changes...')
            : (mode === 'create' ? 'Create Club' : 'Save Changes')}
        </Button>
        <Button
          type="button"
          variant={mode === 'create' ? 'ghost' : 'secondary'}
          size="lg"
          className="w-full"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default ClubForm;
